import { Grid, IconButton, Button, FormControlLabel, Switch, Card, CardContent, Table, TableRow, TableCell, CardHeader, Box, DialogContent, DialogContentText, Dialog, TextField, DialogActions, TableBody, styled, Select, MenuItem, FormControl, InputLabel, Alert } from "@mui/material";
import { Add, Delete, RestoreFromTrash } from "@mui/icons-material";
import React, { useState } from "react";
import FBSwitch from "./FBSwitch";
import { useTranslation } from "react-i18next";
import { NLTooltipWrapper } from "utils/SmallUtils";
import { useRFirebaseDb } from "utils/FirebaseDbWrapper";
import { useNL } from "utils/NLContext";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { DatabaseReference, getDatabase, ref, remove, runTransaction } from "firebase/database";
import FBKeyboardDateTimePicker from "./FBKeyboardDateTimePicker";
import { useTSelector } from "rx/store";
import LinkBox from "./LinkBox";
import { pink } from "@mui/material/colors";


type CustomRegFieldsType = {
  [fieldid: string]: {
    hint: string;
    label: string;
    s?: number;
    required?: boolean;
    type?: React.HTMLInputTypeAttribute;
    muitype?: "text" | "single" | "multiple" | "boolean";
    selectvalues?: string;
    defaultvalue?: string | boolean;
    public?: boolean;
  }
};

type StopRulesType = {
  [fieldid: string]: {
    [value: string]: number
  }
}

export type RegData = {
  publicreglist?: boolean;
  enabled?: boolean;
  email?: boolean;
  emailpublic?: boolean;
  emailrequired?: boolean;
  phone?: boolean;
  phonepublic?: boolean;
  phonerequired?: boolean;
  car?: boolean;
  carpublic?: boolean;
  carrequired?: boolean;
  regend?: number;
  customfields?: CustomRegFieldsType;
  deletedcustomfields?: CustomRegFieldsType;
  stoprules?: StopRulesType;
}

const startStopRegistration = (regref: DatabaseReference | null) => {
  if (!regref) return null;
  runTransaction(regref, (cv) => {
    cv.registration = cv.registration ?? null;
    cv.registrationDisabled = cv.registrationDisabled ?? null;
    if (cv.registration && cv.registrationDisabled)
      cv.registrationDisabled = null;
    if (!cv.registration && !cv.registrationDisabled) {
      cv.registration = { enabled: true };
    } else {
      [cv.registration, cv.registrationDisabled] = [cv.registrationDisabled, cv.registration];
    }
    return cv;
  });
}

const moveRegField = async (registration: DatabaseReference, field: string) => {
  const updateField = (container: Record<string, unknown>, field: string, value: string) => {
    if (value) {
      return { ...(container || {}), [field]: value };
    } else {
      if (container) {
        delete container[field];
        if (Object.keys(container).length === 0) return null; // Return undefined to indicate an empty container
      }
      return container;
    }
  };
  try {
    await runTransaction(registration, (cv) => {
      if (!cv) return;

      const custf = cv.customfields?.[field] ?? null;
      const dcustf = cv.deletedcustomfields?.[field] ?? null;

      if (!custf && !dcustf) return; // Abort transaction if both are null

      // Swap field between the containers
      [cv.customfields, cv.deletedcustomfields] = [
        updateField(cv.customfields, field, custf ? null : dcustf),
        updateField(cv.deletedcustomfields, field, custf)
      ];

      return cv; // Return the modified object to commit changes
    });
  } catch (error) {
    console.error("Error during transaction:", error);
  }
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .deleted-customfield': {
    textDecoration: "line-through",
    backgroundColor: pink[100],
    '&:hover': {
      backgroundColor: pink[200],
    },
  },
}));

const CustomFieldContents: React.FC<{ regdata: RegData, regpath: string }> = ({ regdata, regpath }) => {
  const { t } = useTranslation();
  const { fbUpdate, addUndo } = useNL();
  const rows = Object.entries(regdata.customfields || {}).map(([f, v]) => {
    return Object.assign({ id: f }, v);
  }).concat(
    Object.entries(regdata.deletedcustomfields || {}).map(([f, v]) => {
      return Object.assign({ id: f, deleted: true }, v);
    })
  );
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('reg.customf.header.id')
    },
    {
      field: 'muitype',
      headerName: t('reg.customf.header.type'),
      editable: true,
      type: 'singleSelect',
      valueOptions: [t('reg.muitype.text'), t('reg.muitype.single'), t('reg.muitype.multiple'), t('reg.muitype.bool')],
      valueGetter: (value) => {
        let resp = t('reg.muitype.text');
        if (value === 'single')
          resp = t('reg.muitype.single');
        else if (value === 'multiple')
          resp = t('reg.muitype.multiple');
        else if (value === 'boolean')
          resp = t('reg.muitype.bool');
        return resp;
      },
      valueSetter: (value, row) => {
        let muitype = 'text';
        if (value === t('reg.muitype.single'))
          muitype = 'single';
        else if (value === t('reg.muitype.multiple'))
          muitype = 'multiple';
        else if (value === t('reg.muitype.bool'))
          muitype = 'boolean';

        return { ...row, muitype }
      }
    },
    {
      field: 'selectvalues',
      editable: true,
      headerName: t('reg.customf.header.selectvalues'),
      valueSetter: (selectvalues, row) => {
        if (row.muitype !== "single" && row.muitype !== "multiple") {
          return row;
        }
        return { ...row, selectvalues };
      }
    },
    {
      field: 'label',
      editable: true,
      headerName: t('reg.customf.header.label')

    }, {
      field: 'hint',
      editable: true,
      headerName: t('reg.customf.header.hint')
    }, {
      field: 's',
      editable: true,
      type: 'number',
      headerName: t('reg.customf.header.order')
    }, {
      field: 'public',
      editable: true,
      type: 'boolean',
      headerName: t('reg.customf.header.public')
    }, {
      field: 'required',
      editable: true,
      type: 'boolean',
      headerName: t('reg.customf.header.required')
    }, {
      field: 'delete',
      type: 'actions',
      getActions: (params) => {
        const db = getDatabase();
        let fromRef = ref(db, `${regpath}/customfields/${params.id}`);
        let toRef = ref(db, `${regpath}/deletedcustomfields/${params.id}`);
        if (params.row.deleted) [fromRef, toRef] = [toRef, fromRef];
        return [
          <GridActionsCellItem
            icon={params.row.deleted ? <RestoreFromTrash /> : <Delete />}
            label="Delete"
            onClick={() => {
              const regpathref = ref(db, `${regpath}`);
              moveRegField(regpathref, params.id.toString());
              addUndo("regstate", params.row.deleted ? t('reg.undo.restorecustomf' as any) : t('reg.undo.deletecustomf'), () => {
                moveRegField(regpathref, params.id.toString());
              })

            }}
          />]
      }
    }];
  return <Box sx={{ height: 300, width: '100%' }}>
    <StyledDataGrid
      rows={rows}
      columns={columns}
      editMode="row"
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 5,
          },
        },
      }}
      getRowClassName={(params) => `${params.row.deleted ? "deleted" : "active"}-customfield`}
      processRowUpdate={(newRow, oldRow) => {
        let updateData: { [k: string]: string | number | boolean } = {}
        Object.keys(newRow).filter(f => f !== 'deleted').forEach(f => {
          if (newRow[f] === oldRow[f]) return;
          updateData[f] = newRow[f];
        });
        const fieldskey = newRow.deleted ? "deletedcustomfields" : "customfields";
        if (Object.keys(updateData).length > 0) {
          if (typeof updateData?.selectvalues === "string") {
            updateData.selectvalues = updateData.selectvalues.split(",").map(v => v.trim()).join(",");
          }
          fbUpdate(ref(getDatabase(), `${regpath}/${fieldskey}/${oldRow["id"]}`), updateData, t('reg.undo.updatedcustomf'))
        }
        return newRow;
      }}
      pageSizeOptions={[5]}
      disableRowSelectionOnClick
    />
  </Box>;
}

const AddCustomFieldButton: React.FC<{ regdata?: RegData | null, regpath: string }> = ({ regdata, regpath }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errormsg, setErrorMsg] = useState<string>();
  const klassid = useTSelector(s => s.event.klassid);
  const { fbSet } = useNL();
  const { t } = useTranslation();

  return (
    <>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{
        component: 'form',
        onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);
          const fieldid = formData.get("fieldid")?.toString();
          if (fieldid === undefined) return false;
          const isValidFirebaseKey = /^[^.$[\]#]{1,768}$/i.test(fieldid);
          if (!isValidFirebaseKey) {
            setErrorMsg(t("reg.error.badcharacter"));
            return false;
          }
          if (regdata?.customfields?.hasOwnProperty(fieldid) || regdata?.deletedcustomfields?.hasOwnProperty(fieldid)) {
            setErrorMsg(t("reg.error.duplicatefield" as any));
            return false;
          }

          fbSet(`${regpath}/customfields/${fieldid}`, { label: fieldid });
          setDialogOpen(false);
        },
      }} >
        <DialogContent>
          <DialogContentText>{t('message.entercustomfieldid')}</DialogContentText>
          <Box display="flex" flexDirection="column">
            <TextField
              autoFocus
              required
              margin="dense"
              id="fieldid"
              name="fieldid"
              label={t('reg.fieldid.label')}
              type="text"
              fullWidth
              error={Boolean(errormsg)}
              helperText={errormsg}
              variant="standard"
            />
            <Button sx={{ alignSelf: "center" }} variant="contained" onClick={() => {
              remove(ref(getDatabase(), `${regpath}/deletedcustomfields/klass`));
              fbSet(`${regpath}/customfields/klass`, {
                label: "Klass", hint: "Valik klass", muitype: "multiple",
                selectvalues: klassid ? ["--", ...Object.keys(klassid).filter(k => !klassid[k])].join(",") : ""
              });
              setDialogOpen(false)
            }
            }>{t('button.reg.addclass' as any)}</Button>

          </Box>

        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDialogOpen(false)}>{t('button.cancel')}</Button>
          <Button variant="contained" type="submit">{t('button.submit')}</Button>
        </DialogActions>
      </Dialog>
      <IconButton onClick={() => {
        setErrorMsg(undefined);
        setDialogOpen(true);
      }}><Add /></IconButton></>
  );
}


const StopRulesContents: React.FC<{ regdata: RegData, regpath: string }> = ({ regdata, regpath }) => {
  const { t } = useTranslation();
  const { fbUpdate, fbRemove } = useNL();
  if (!regdata.stoprules) return null;
  const rows = Object.entries(regdata.stoprules).flatMap(([field, values]) =>
    Object.entries(values).map(([value, count]) => ({
      id: field + "/" + value,
      field: field,
      value: value,
      count: count
    }))
  );
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('reg.customf.header.id')
    },
    {
      field: 'field',
      headerName: t('reg.customf.header.type'),
    },
    {
      field: 'value',
      headerName: t('reg.customf.header.selectvalues'),
    },
    {
      field: 'count',
      headerName: t('reg.customf.header.label')
    }, {
      field: 'delete',
      type: 'actions',
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => {
              const db = getDatabase();
              fbRemove(ref(db, `${regpath}/stoprules/${params.id}`))
            }}
          />]
      }
    }];
  return <Box sx={{ height: 300, width: '100%' }}>
    <StyledDataGrid
      rows={rows}
      columns={columns}
      editMode="row"
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 5,
          },
        },
      }}
      getRowClassName={(params) => `${params.row.deleted ? "deleted" : "active"}-customfield`}
      processRowUpdate={(newRow, oldRow) => {
        let updateData: { [k: string]: string | number | boolean } = {}
        Object.keys(newRow).filter(f => f !== 'deleted').forEach(f => {
          if (newRow[f] === oldRow[f]) return;
          updateData[f] = newRow[f];
        });
        const fieldskey = newRow.deleted ? "deletedcustomfields" : "customfields";
        if (Object.keys(updateData).length > 0) {
          fbUpdate(ref(getDatabase(), `${regpath}/${fieldskey}/${oldRow["id"]}`), updateData, t('reg.undo.updatedcustomf'))
        }
        return newRow;
      }}
      pageSizeOptions={[5]}
      disableRowSelectionOnClick
    />
  </Box>;
}

const AddStopRuleButton: React.FC<{ regdata?: RegData | null, regpath: string }> = ({ regdata, regpath }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [field, setField] = useState<string>();
  const [value, setValue] = useState<string>();
  const [count, setCount] = useState<number>();
  const { fbSet } = useNL();
  const { t } = useTranslation();

  const cf = regdata?.customfields;
  if (!cf) return null;
  const fieldkeys = Object.keys(cf);
  return <>
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} >
      <DialogContent>
        <DialogContentText>{t('message.enterstoprule' as any)}</DialogContentText>
        <FormControl sx={{ m: 1, minWidth: 120 }}>
          <InputLabel>{t('label.regstoprule.label.field' as any)}</InputLabel>

          <Select
            value={field ?? ""}
            label={t('label.regstoprule.labelfield' as any)}
            onChange={(v) => { setValue(undefined); setField(v.target.value) }}
          >
            {fieldkeys.map(fk => <MenuItem value={fk}>{cf[fk].label}</MenuItem>)}
          </Select>
        </FormControl>
        {field && ["multiple", "single", "boolean"].includes(cf[field].muitype ?? "") &&
          <FormControl sx={{ m: 1, minWidth: 120 }}>
            <InputLabel>{t('label.regstoprule.label.value' as any)}</InputLabel>

            <Select
              value={value ?? ""}
              label={t('label.regstoprule.label.value' as any)}
              onChange={(v) => setValue(v.target.value)}
            >
              {cf[field].muitype === "boolean" ? ["true", "false"].map(fk => <MenuItem value={fk}>{fk}</MenuItem>) :
                cf[field].selectvalues?.split(",").map(v => v.trim()).map(fk => <MenuItem value={fk}>{fk}</MenuItem>)}
            </Select>
          </FormControl>
        }
        {field && (cf[field].muitype === "text" || cf[field].muitype === undefined) &&
          <FormControl sx={{ m: 1, maxWidth: 120 }}>
            <TextField label={t('label.regstoprule.label.value' as any)}
              variant="outlined"
              value={value}
              onChange={(v) => setValue(v.target.value)}
            />
          </FormControl>
        }
        {field && value &&
          <FormControl sx={{ m: 1, maxWidth: 120 }}>
            <TextField label={t('label.regstoprule.label.count' as any)}
              variant="outlined"
              type="number"
              value={count}
              onChange={(v) => setCount(parseInt(v.target.value))}
            />
          </FormControl>}

      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => {
          setDialogOpen(false)
          setField(undefined);
          setValue(undefined);
          setCount(undefined);
        }}>{t('button.cancel')}</Button>
        <Button disabled={!field || !value || !count} variant="contained" type="submit"
          onClick={() => {
            setDialogOpen(false)
            fbSet(`${regpath}/stoprules/${field}/${value}`, count);
            setField(undefined);
            setValue(undefined);
            setCount(undefined);
          }}
        >{t('button.submit')}</Button>
      </DialogActions>
    </Dialog>
    <IconButton onClick={() => {
      setDialogOpen(true);
    }}><Add /></IconButton></>;
}

const EventSettingsRegistration: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation();
  const regpath = `/eventsdata/${eventId}/data/registration`;
  const regdata = useRFirebaseDb<RegData>(regpath)
  const joinrequiresconfirmation = useTSelector(s => s.event.joinrequiresconfirmation);
  const klassid = useTSelector(s => s.event.klassid);
  const { addUndo } = useNL();
  const conf = useTSelector((state) => state.conf);
  const noregdata = !Boolean(regdata);
  const badclasses = regdata?.customfields?.klass?.selectvalues?.split(",")?.map(v => v.trim())?.filter(e => !["-", "--"].includes(e))?.filter(e => !klassid?.hasOwnProperty(e));
  return (
    <>
      <Grid container spacing={2} alignItems="center" direction="row" sx={{ paddingBottom: 2 }}>
        <Grid size={12}>
          <LinkBox link={`https://${conf.hostname}/${eventId}/reg`} />
        </Grid>
        <Grid>

          <NLTooltipWrapper
            tkey={`fbf.enablereg.tooltip`}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={!noregdata}
                  onChange={
                    (_event, checked) => {
                      const regref = ref(getDatabase(), regpath).parent;
                      startStopRegistration(regref);
                      addUndo("regstate", checked ? t('fbf.enablereg.undomsgforset') : t('fbf.enablereg.undomsgforunset'), () => {
                        startStopRegistration(regref);
                      })
                    }
                  }
                />
              }
              label={t(`fbf.enablereg.label`)}
            />
          </NLTooltipWrapper>
        </Grid>
        <Grid>
          <FBSwitch disabled={noregdata} id="reglistpublic" path={`/eventsdata/${eventId}/data/registration/publicreglist`} />
        </Grid>
        <Grid>
          <FBSwitch disabled={!joinrequiresconfirmation || !regdata?.publicreglist} id="reglistallowedonly" path={`/eventsdata/${eventId}/data/registration/reglistallowedonly`} />
        </Grid>
        <Grid>
          <FBKeyboardDateTimePicker disabled={noregdata} ampm={false} path={`/eventsdata/${eventId}/data/registration/regend`} id="regend"
            format="DD/MM/YYYY HH:mm"
            slotProps={{
              actionBar: {
                actions: ["clear", "today", "accept", "cancel"],
              },
            }} />
        </Grid>
      </Grid>
      {!noregdata && (
        <Grid container spacing={2} alignItems="center" direction="row">
          <Grid>
            <Card>
              <CardHeader title={t('title.predefinedfields')} />
              <CardContent>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><FBSwitch id="emailreg" path={`${regpath}/email`} /></TableCell>
                      <TableCell><FBSwitch disabled={!regdata?.publicreglist} id="regfieldpublic" path={`${regpath}/emailpublic`} /></TableCell>
                      <TableCell><FBSwitch id="regfieldrequired" path={`${regpath}/emailrequired`} /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><FBSwitch id="phonereg" path={`${regpath}/phone`} /></TableCell>
                      <TableCell><FBSwitch disabled={!regdata?.publicreglist} id="regfieldpublic" path={`${regpath}/phonepublic`} /></TableCell>
                      <TableCell><FBSwitch id="regfieldrequired" path={`${regpath}/phonerequired`} /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><FBSwitch id="carreg" path={`${regpath}/car`} /></TableCell>
                      <TableCell><FBSwitch disabled={!regdata?.publicreglist} id="regfieldpublic" path={`${regpath}/carpublic`} /></TableCell>
                      <TableCell><FBSwitch id="regfieldrequired" path={`${regpath}/carrequired`} /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          <Grid>
            <Card>
              <CardHeader title={(<><span>{t('title.customfields')}</span><AddCustomFieldButton regpath={regpath} regdata={regdata} /></>)} />
              <CardContent>
                {badclasses?.length && <Alert severity="warning">{t("reg.error.badclasses", { classes: badclasses })}</Alert>}
                {regdata && <CustomFieldContents regdata={regdata} regpath={regpath} />}
              </CardContent>
            </Card>
          </Grid>
          {Object.keys(regdata?.customfields || {}).length > 0 && <Grid>
            <Card>
              <CardHeader title={(<><span>{t('title.regstoprules' as any)}</span><AddStopRuleButton regpath={regpath} regdata={regdata} /></>)} />
              <CardContent>
                {regdata && <StopRulesContents regdata={regdata} regpath={regpath} />}
              </CardContent>
            </Card>
          </Grid>}
        </Grid>)}

    </>
  );
};

export default EventSettingsRegistration;
